import { message, Spin } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { getCertificate } from '../../../../api/aopsHR.api'
import AopsCommonModalLayout from '../../../../components/apos/modal/AopsCommonModalLayout'
import { employmentStatusMap } from '../../orgChart/utils/employmentMappings'
import { generateEmploymentCertificatePdf } from '../../orgChart/utils/generateEmploymentCertificatePdf'
import '../../orgChart/orgChart.scss'
import DocumentViewer from './DocumentViewer'

const PURPOSE_TYPE_TO_LABEL = {
  FINANCIAL: '금융기관 제출',
  GOVERNMENT: '관공서 제출',
  OTHER: '기타',
  EXTERNAL_ORG: '기타',
}

const CertificateViewerModal = ({
  open,
  onClose,
  contract,
  personalInfo = null,
}) => {
  const [pdfUrl, setPdfUrl] = useState(null)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [downloadFileName, setDownloadFileName] = useState('')

  useEffect(() => {
    if (!open) {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl)
      }
      setPdfUrl(null)
      setDownloadFileName('')
      return
    }
    if (!contract?.id) {
      return
    }

    const loadPdf = async () => {
      setIsGeneratingPdf(true)
      setPdfUrl(null)
      setDownloadFileName('')
      try {
        const res = await getCertificate(contract.id)
        const data = res?.data
        if (!data) {
          message.error('증명서 정보를 불러올 수 없습니다.')
          setIsGeneratingPdf(false)
          return
        }
        const purpose =
          PURPOSE_TYPE_TO_LABEL[data.purposeType] ||
          data.purposeTypeDisplayName ||
          '기타'
        const pdfData = {
          certificateNo: data.certificateNo || '',
          issueDate: data.issuedAt ? new Date(data.issuedAt) : new Date(),
          employeeName: data.employeeName || '',
          birthDate:
            data.birthDate ||
            personalInfo?.birthDate ||
            personalInfo?.birthday ||
            null,
          address: data.address || personalInfo?.address || '',
          employeeType:
            data.rank ||
            data.title ||
            data.employeeType ||
            (data.employmentStatus &&
              employmentStatusMap[data.employmentStatus]) ||
            '재직',
          employmentStartDate: data.joinDate || null,
          jobGroup: data.position || data.jobGroup || '',
          department: data.department || '',
          purpose,
        }
        const pdfBlob = await generateEmploymentCertificatePdf(pdfData)
        const url = window.URL.createObjectURL(pdfBlob)
        setPdfUrl(url)
        setDownloadFileName(
          `재직증명서_${data.employeeName || '직원'}_${dayjs().format('YYYYMMDD')}.pdf`,
        )
      } catch (error) {
        console.error('재직증명서 PDF 로드 오류:', error)
        message.error(
          error?.response?.data?.message ||
            '재직증명서를 불러오는 중 오류가 발생했습니다.',
        )
      } finally {
        setIsGeneratingPdf(false)
      }
    }

    loadPdf()
  }, [open, contract?.id])

  const handleDownload = () => {
    if (!pdfUrl || !downloadFileName) {
      message.warning('PDF가 준비되지 않았습니다.')
      return
    }
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = downloadFileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    message.success('재직증명서가 다운로드되었습니다.')
  }

  const handleClose = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl)
    }
    setPdfUrl(null)
    setDownloadFileName('')
    onClose?.()
  }

  return (
    <AopsCommonModalLayout
      open={open}
      onClose={handleClose}
      title="재직증명서 보기"
    >
      <div className="contract-sign-modal-content">
        <div className="contract-viewer-label contract-viewer-label-column">
          <span className="contract-status-badge">
            {contract?.status || '완료'}
          </span>
          <span className="contract-title-text">
            {contract?.contractName || '재직증명서'}
          </span>
        </div>

        {isGeneratingPdf ? (
          <div className="contract-viewer-container">
            <div className="contract-viewer-loading">
              <Spin size="small" />
              <div className="contract-viewer-loading-text">
                잠시만 기다려주세요
              </div>
            </div>
          </div>
        ) : pdfUrl ? (
          <div className="contract-viewer-container">
            <DocumentViewer
              pdfUrl={pdfUrl}
              downloadFileName={downloadFileName}
              onDownload={handleDownload}
            />
          </div>
        ) : (
          <div className="contract-viewer-container">
            <div className="contract-viewer-error">
              <div>PDF를 불러올 수 없습니다.</div>
              <div className="contract-viewer-error-text">
                PDF 생성에 실패했습니다. 다시 시도해주세요.
              </div>
            </div>
          </div>
        )}
      </div>
    </AopsCommonModalLayout>
  )
}

export default CertificateViewerModal
